import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Sun, LayoutDashboard, DollarSign, Monitor, CalendarDays } from "lucide-react";
import { useQuinzenaAtiva, useTarefasByData } from "@/hooks/use-equipe";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

import { PageHeader } from "@/components/layout/page-header";
const STATUS_COLORS: Record<string, string> = {
  ABERTA: "bg-atencao-suave text-atencao border-atencao/30",
  FECHADA: "bg-info-suave text-info border-info/30",
  PAGA: "bg-sucesso-suave text-sucesso border-sucesso/30",
};

export default function EquipeDashboard() {
  const [, setLocation] = useLocation();
  const hoje = format(new Date(), "yyyy-MM-dd");
  const { data: quinzena, isLoading: qLoading } = useQuinzenaAtiva();
  const { data: tarefasHoje = [], isLoading: tLoading } = useTarefasByData(hoje);

  const concluidas = tarefasHoje.filter(t => t.status === "CONCLUIDA").length;

  const atalhos = [
    // Mesmo tratamento nos quatro: são links para telas, não estados. Cor por
    // atalho fazia quatro significados onde só existe um. (Igual ao hub de
    // Relatórios, unificado no #173.)
    { label: "Hoje no Campo", icon: Sun, path: "/equipe/hoje" },
    { label: "Plano da Semana", icon: LayoutDashboard, path: "/equipe/plano" },
    { label: "Financeiro", icon: DollarSign, path: "/equipe/financeiro" },
    { label: "Modo TV", icon: Monitor, path: "/equipe/tv" },
  ];

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        titulo="Dashboard"
        descricao={format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <Users className="h-8 w-8 text-muted-foreground" />
            <div>
              {tLoading ? <Skeleton className="h-6 w-12" /> : <p className="text-2xl font-bold">{new Set(tarefasHoje.map(t => t.funcionarioId)).size}</p>}
              <p className="text-xs text-muted-foreground">Em campo hoje</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <CalendarDays className="h-8 w-8 text-muted-foreground" />
            <div>
              {tLoading ? <Skeleton className="h-6 w-12" /> : <p className="text-2xl font-bold">{tarefasHoje.length}</p>}
              <p className="text-xs text-muted-foreground">Tarefas planejadas</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <Sun className="h-8 w-8 text-muted-foreground" />
            <div>
              {tLoading ? <Skeleton className="h-6 w-12" /> : <p className="text-2xl font-bold">{concluidas}</p>}
              <p className="text-xs text-muted-foreground">Concluídas</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quinzena ativa */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Quinzena Atual</CardTitle>
        </CardHeader>
        <CardContent>
          {qLoading ? <Skeleton className="h-12" /> : quinzena ? (
            <div className="flex items-center justify-between">
              <div>
                <Badge variant="outline" className={STATUS_COLORS[quinzena.status]}>
                  {quinzena.status}
                </Badge>
                <p className="text-sm text-muted-foreground mt-1">
                  {format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM")} – {format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM/yyyy")}
                </p>
              </div>
              <p className="text-xl font-bold">R$ {parseFloat(quinzena.totalBruto).toFixed(2)}</p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma quinzena aberta.</p>
          )}
        </CardContent>
      </Card>

      {/* Atalhos */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {atalhos.map(a => (
          <Button key={a.path} variant="outline" className="h-20 flex-col gap-2" onClick={() => setLocation(a.path)}>
            <a.icon className="h-6 w-6 text-muted-foreground" />
            <span className="text-xs font-medium">{a.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}
